import {
  ResponsiveContainer, ComposedChart, Bar, Line, Area, AreaChart,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell, PieChart, Pie,
} from "recharts";

const FOREST = "#2A4B38";
const TERRACOTTA = "#D67049";
const LINE = "#E8E6E1";
const MUTE = "#73706A";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const PIE_COLORS = ["#2A4B38", "#D67049", "#6B8F71", "#E0A458", "#4A6B8A", "#B0603B", "#8FA98F", "#C98A5E", "#3E5C4A", "#A9C0AB"];
const TYPE_LABEL = (t) => (t || "other").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const money0 = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const money2 = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TT = ({ active, payload, label, roi }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-line rounded-lg shadow-sm px-3 py-2 text-xs">
      <p className="font-medium text-ink mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }} className="tabular-nums">
          {p.name}: {roi ? `${Number(p.value || 0).toFixed(1)}%` : money2(p.value)}
        </p>
      ))}
    </div>
  );
};

const ChartCard = ({ title, subtitle, children, testid }) => (
  <div data-testid={testid} className="bg-white border border-line rounded-xl p-4">
    <p className="text-sm font-medium text-ink">{title}</p>
    {subtitle && <p className="text-xs text-mute mt-0.5 mb-2">{subtitle}</p>}
    <div className="mt-2">{children}</div>
  </div>
);

export const ExpenseCharts = ({ summary, year }) => {
  const s = summary || {};
  const monthly = MONTHS.map((m, i) => ({
    month: m,
    income: (s.income_by_month || [])[i] || 0,
    expenses: (s.by_month || [])[i] || 0,
    net: (s.net_by_month || [])[i] ?? null,
    cumulative: (s.cumulative_net || [])[i] ?? null,
    roi: s.roi_by_month ? (s.roi_by_month[i] ?? null) : null,
  }));
  const byType = (s.by_type || []).map((t) => ({ name: TYPE_LABEL(t.type), value: t.amount, type: t.type }));
  const hasRoi = Array.isArray(s.roi_by_month);

  return (
    <div className="space-y-3">
      <div className="grid lg:grid-cols-2 gap-3">
        <ChartCard testid="chart-monthly-net" title={`Income, expenses & net · ${year}`} subtitle="Bars show rent collected vs. expenses each month; the line is monthly net cash flow.">
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={monthly} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={LINE} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: MUTE }} axisLine={{ stroke: LINE }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: MUTE }} axisLine={false} tickLine={false} tickFormatter={money0} width={56} />
              <Tooltip content={<TT />} />
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
              <Bar dataKey="income" name="Income" fill={FOREST} radius={[3, 3, 0, 0]} maxBarSize={18} />
              <Bar dataKey="expenses" name="Expenses" fill={TERRACOTTA} radius={[3, 3, 0, 0]} maxBarSize={18} />
              <Line type="monotone" dataKey="net" name="Net" stroke="#1A1A1A" strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testid="chart-cumulative-net" title={`Cumulative net cash flow · ${year}`} subtitle="Running total of net cash flow across the year — the property's bottom line over time.">
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={monthly} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="netFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={FOREST} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={FOREST} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={LINE} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: MUTE }} axisLine={{ stroke: LINE }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: MUTE }} axisLine={false} tickLine={false} tickFormatter={money0} width={56} />
              <Tooltip content={<TT />} />
              <Area type="monotone" dataKey="cumulative" name="Cumulative net" stroke={FOREST} strokeWidth={2} fill="url(#netFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <ChartCard testid="chart-roi-trend" title={`Annualized ROI trend · ${year}`} subtitle={hasRoi ? "Estimated annual return on the purchase price as the year builds up." : "Add a purchase price in Property financials to see ROI."}>
          {hasRoi ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={monthly} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="roiFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={TERRACOTTA} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={TERRACOTTA} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={LINE} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: MUTE }} axisLine={{ stroke: LINE }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: MUTE }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} width={44} />
                <Tooltip content={<TT roi />} />
                <Area type="monotone" dataKey="roi" name="ROI" stroke={TERRACOTTA} strokeWidth={2} fill="url(#roiFill)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-sm text-mute">No purchase price on file yet.</div>
          )}
        </ChartCard>

        <ChartCard testid="chart-by-type" title={`Expenses by type · ${year}`} subtitle="Where your operating spend went this year.">
          {byType.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-sm text-mute">No expenses recorded for {year}.</div>
          ) : (
            <div className="flex items-center gap-3">
              <ResponsiveContainer width="55%" height={220}>
                <PieChart>
                  <Pie data={byType} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={44} outerRadius={78} paddingAngle={2}>
                    {byType.map((entry, i) => <Cell key={entry.type} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<TT />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 min-w-0 space-y-1 max-h-[210px] overflow-auto">
                {byType.map((t, i) => (
                  <div key={t.type} data-testid={`legend-${t.type}`} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex items-center gap-1.5 min-w-0"><span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} /><span className="text-ink truncate">{t.name}</span></span>
                    <span className="font-medium text-ink tabular-nums shrink-0">{money2(t.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
};
